/**
 * Rolling date ranges — "last 7 days", "month to date" — on the store's day.
 *
 * Written once and used by both faces of reporting: the server resolves a
 * saved custom report's column (decision 210) and the register fills a
 * report's From and To from a quick pick (2026-10-16: "on reports i think some
 * quick select last 7 days, last 30 days, month to date and last month could
 * be helpful"). Two copies would disagree about where last week starts within
 * a season.
 *
 * Calendar arithmetic only, on YYYY-MM-DD at noon UTC, so no clock and no time
 * zone can move a day: the caller says what today is (decisions 169, 183).
 * Weeks start on Monday.
 */

export const DATE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'lastWeek', label: 'Last week' },
  { key: 'monthToDate', label: 'Month to date' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'yearToDate', label: 'Year to date' },
  { key: 'lastYear', label: 'Last year' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'last365', label: 'Last 365 days' },
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number]['key'];

export interface DayRange {
  readonly from: string;
  readonly to: string;
}

const pad = (part: number) => String(part).padStart(2, '0');

/** A day moved a number of days. */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** A day moved whole years; 29 February lands on the 28th in a common year. */
export function shiftYears(day: string, years: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const year = y + years;
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return `${year}-${pad(m)}-${pad(Math.min(d, last))}`;
}

/** A day moved whole months; the 31st lands on the month's last day. */
export function shiftMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${pad(month)}-${pad(Math.min(d, last))}`;
}

/** 0 for Sunday through 6 for Saturday. */
export function weekdayOf(day: string): number {
  return new Date(`${day}T12:00:00Z`).getUTCDay();
}

/** A rolling preset on the store's day `today` (YYYY-MM-DD). */
export function presetRange(preset: DatePreset, today: string): DayRange {
  const [y, m] = today.split('-').map(Number) as [number, number, number];
  const weekday = (weekdayOf(today) + 6) % 7;
  const monthStart = `${y}-${pad(m)}-01`;
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case 'thisWeek':
      return { from: addDays(today, -weekday), to: today };
    case 'lastWeek':
      return { from: addDays(today, -weekday - 7), to: addDays(today, -weekday - 1) };
    case 'monthToDate':
      return { from: monthStart, to: today };
    case 'lastMonth': {
      const end = addDays(monthStart, -1);
      return { from: `${end.slice(0, 7)}-01`, to: end };
    }
    case 'yearToDate':
      return { from: `${y}-01-01`, to: today };
    case 'lastYear':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'last365':
      return { from: addDays(today, -364), to: today };
  }
}

/** Which preset a From and To are exactly, on `today` — so a quick pick can show as chosen. */
export function presetMatching(
  range: { from: string | null; to: string | null },
  today: string,
  among: readonly DatePreset[] = DATE_PRESETS.map((preset) => preset.key),
): DatePreset | null {
  if (range.from === null || range.to === null) return null;
  for (const preset of among) {
    const days = presetRange(preset, today);
    if (days.from === range.from && days.to === range.to) return preset;
  }
  return null;
}
